import { Skeleton, Symbol, Toaster, Wordmark, cn } from '@westhetics/ui'
import type { FormEvent, ReactNode } from 'react'

/**
 * O leiaute das telas de acesso, fora da casca (docs/areas/autenticacao.md; decisão D01 e P-AUT-12):
 * formulário em pampas à esquerda, painel em ink com a tagline à direita, sem imagem. Sem
 * `onSubmit` a coluna não é formulário: serve ao estado que só informa e oferece a saída.
 */
export function AuthLayout({
  eyebrow = 'ACESSO',
  title,
  onSubmit,
  children,
}: {
  eyebrow?: string
  title: string
  onSubmit?: (event: FormEvent) => void
  children: ReactNode
}) {
  const column = 'mx-auto flex w-full max-w-[340px] flex-1 flex-col justify-center py-8'
  const content = (
    <>
      <div className="font-body text-label text-[11px] leading-none font-light tracking-[.22em]">
        {eyebrow}
      </div>
      <h1 className="font-display text-ink mt-3 text-[28px] leading-[1.15] font-light">{title}</h1>
      {children}
    </>
  )
  return (
    <>
      {/* Fora da casca não há outro aviso: é aqui que "Senha salva." aparece antes da tela seguinte.
          Fica fora da grade, onde viraria uma terceira coluna. */}
      <Toaster />
      <div className="bg-pampas grid min-h-dvh grid-cols-[repeat(auto-fit,minmax(320px,1fr))]">
        <div className="flex min-h-[560px] flex-col px-8 py-10">
          <Wordmark className="text-ink h-[14px] w-auto self-start" />
          {onSubmit ? (
            <form onSubmit={onSubmit} className={column} noValidate>
              {content}
            </form>
          ) : (
            <div className={column}>{content}</div>
          )}
          <div className="font-body text-label text-[11px] leading-none font-light">
            Sessão de 12 horas · © 2026 Westhetics
          </div>
        </div>

        <div className="bg-ink text-pampas flex min-h-[420px] flex-col justify-between px-10 py-10">
          <Symbol className="h-[26px] w-auto self-start" />
          <div>
            <div className="h-px w-[52px] bg-[rgba(240,238,233,.4)]" />
            <p className="font-display mt-6 max-w-[380px] text-[32px] leading-[1.15] font-light">
              A beleza deve ser segura.
            </p>
            <p className="font-body mt-5 max-w-[360px] text-[14px] leading-[1.7] font-light">
              Nascemos da convicção de que a segurança jurídica pode e deve fazer parte de uma boa
              experiência.
            </p>
          </div>
          <div className="font-body text-[11px] leading-none font-light tracking-[.2em]">
            A PROTEÇÃO POR TRÁS DA SUA EXCELÊNCIA
          </div>
        </div>
      </div>
    </>
  )
}

/** O campo das telas de acesso: só o fio de baixo, como no login do protótipo. */
export const authInput =
  'block w-full border-0 border-b border-[rgba(26,26,26,.32)] bg-transparent px-0 py-3 font-body text-[14px] leading-none font-light text-ink outline-none placeholder:text-label-soft focus:border-ink aria-invalid:border-plum'

/** Abaixo de `desk` o alvo de toque é de 48px (07-responsividade): a área cresce, o desenho não. */
const touchArea =
  "relative max-desk:after:absolute max-desk:after:inset-x-0 max-desk:after:top-1/2 max-desk:after:h-touch max-desk:after:-translate-y-1/2 max-desk:after:content-['']"

/** O texto de apoio das telas de acesso: a linha abaixo do botão, a explicação abaixo do título. */
export const authNote = 'font-body text-label text-[12px] leading-[1.6] font-light'

/** A ação secundária em texto ("Voltar para entrar com senha"), sozinha na linha. */
export const authLink = `font-body text-label hover:text-ink border-hairline-heavy cursor-pointer self-start border-b pb-[2px] text-[12px] leading-none font-light ${touchArea}`

/** O link dentro de uma frase ("Esqueceu a senha? Entrar com código"). */
export const authInlineLink = `text-ink border-hairline-heavy hover:border-ink cursor-pointer border-b pb-px ${touchArea}`

/** A linha abaixo do campo: a dica, ou o erro no lugar dela (nasce ao sair do campo). */
export function FieldNote({
  id,
  error,
  hint,
}: {
  id: string
  error?: string | null
  hint?: ReactNode
}) {
  if (!error && !hint) return null
  return (
    <p
      id={id}
      role={error ? 'alert' : undefined}
      className={cn(
        'font-body mt-2 text-[12px] leading-[1.5] font-light',
        error ? 'text-plum' : 'text-label',
      )}
    >
      {error ?? hint}
    </p>
  )
}

/** Carregando, com a forma do formulário: rótulo, campo e botão. Nunca spinner. */
export function AuthSkeleton({ fields = 1 }: { fields?: number }) {
  return (
    <div role="status" aria-label="Carregando" className="mt-8 flex flex-col">
      {Array.from({ length: fields }, (_, index) => (
        <div key={index} className={index > 0 ? 'mt-6' : undefined}>
          <Skeleton className="h-[11px] w-[96px]" />
          <Skeleton className="mt-3 h-[38px] w-full" />
        </div>
      ))}
      <Skeleton className="mt-7 h-12 w-full" />
    </div>
  )
}

/** Rótulo do campo de senha com o "mostrar" à direita. */
export function PasswordLabel({
  htmlFor,
  shown,
  onToggle,
  toggleLabel,
  className,
  children,
}: {
  htmlFor: string
  shown: boolean
  onToggle: () => void
  /** Nome acessível do "mostrar", quando a tela tem mais de um campo de senha. */
  toggleLabel?: string
  className?: string
  children: ReactNode
}) {
  return (
    <div className={cn('flex items-baseline justify-between', className)}>
      <label htmlFor={htmlFor} className="eyebrow">
        {children}
      </label>
      <button
        type="button"
        onClick={onToggle}
        aria-label={toggleLabel}
        aria-pressed={shown}
        className="font-body text-eyebrow text-label hover:text-ink cursor-pointer font-light tracking-normal"
      >
        {shown ? 'ocultar' : 'mostrar'}
      </button>
    </div>
  )
}
